import { useEffect, useState } from "react";
import SidebarNav from "../../components/MasterSideBar";
import TopBar from "../../components/TopbarNav";
import ProjectFormModal from "../../components/addprojectmodal";
import DirectorProjectCalendar from "../../components/programDirector/DirectorProjectCalendar";
import PremiumHeader from "../../components/common/PremiumHeader";
import PremiumStatCard from "../../components/common/PremiumStatCard";
import PremiumCard from "../../components/common/PremiumCard";
import { motion, AnimatePresence } from "framer-motion";

import {
  getProjectStats,
  getDonorStats,
  getDisciplinaryStats,
  getDisciplinaryStatsByDepartment,
  getLeaveStats,
  getProjects,
} from "../../api/programDirApi";
import ReactECharts from "echarts-for-react";
import { 
  BarChart2, Clock, CheckCircle, PauseCircle, 
  Calendar, FileText, AlertCircle, Plus, LayoutPanelTop, 
  PieChart as PieChartIcon, TrendingUp, X, Activity, UserPlus
} from "lucide-react";

/* -------------------------------------------------------------------------- */
/* Operational Stats (Mock)                                                   */
/* -------------------------------------------------------------------------- */
const operationalStats = {
  requisitions: {
    pending: 5,
    urgent: 2,
    avgApprovalDays: 3,
  },
};

/* -------------------------------------------------------------------------- */
/* Helper Components                                                          */
/* -------------------------------------------------------------------------- */
const StatusBadge = ({ status }) => {
  const statusMap = {
    Planning: "bg-blue-100/50 text-blue-700",
    "In Progress": "bg-orange-100/50 text-orange-700",
    "On Hold": "bg-yellow-100/50 text-yellow-700",
    Completed: "bg-green-100/50 text-green-700",
    Cancelled: "bg-red-100/50 text-red-700",
    Archived: "bg-gray-100/50 text-gray-700",
  };

  const badgeClass = statusMap[status] || "bg-gray-100/50 text-gray-700";
  return (
    <span className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${badgeClass} backdrop-blur-sm border border-current/10`}>
      {status}
    </span>
  );
};

const ProgressBar = ({ progress }) => (
  <div className="flex items-center gap-4 w-full">
    <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden shadow-inner">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${progress}%` }}
        transition={{ duration: 1, ease: "easeOut" }}
        className="bg-orange-600 h-2 rounded-full shadow-sm"
      />
    </div>
    <span className="text-[10px] font-black text-gray-800 w-10 tabular-nums text-right">{progress}%</span>
  </div>
);

/* -------------------------------------------------------------------------- */
/* Main Dashboard Page                                                        */
/* -------------------------------------------------------------------------- */
export default function ProgramsDirectorDashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [projectStats, setProjectStats] = useState({
    totalProjects: 0, overdue: 0, startingSoon: 0, Planning: 0, Completed: 0, "On Hold": 0, "In Progress": 0,
  });
  const [donorStats, setDonorStats] = useState({
    totalDonationsAllTime: 0, totalDonationsThisYear: 0, totalActiveDonors: 0, averageDonation: 0,
  });
  const [disciplinaryStats, setDisciplinaryStats] = useState({
    openCases: 0, pending: 0, resolved: 0, resolutionRatePercent: 0,
  });
  const [deptDisciplinaryStats, setDeptDisciplinaryStats] = useState([]);
  const [leaveStats, setLeaveStats] = useState({
    totalRequests: 0, pendingRequests: 0, approvedRequests: 0, rejectedRequests: 0,
  });

  const projectsPerPage = 5;
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [projectsStats, donors, disciplinary, deptDisciplinary, leave, projectsList] = await Promise.all([
          getProjectStats(), getDonorStats(), getDisciplinaryStats(),
          getDisciplinaryStatsByDepartment(), getLeaveStats(), getProjects(),
        ]);

        setProjectStats(projectsStats);
        setDonorStats(donors);
        setDisciplinaryStats(disciplinary);
        setLeaveStats(leave);
        setDeptDisciplinaryStats(deptDisciplinary);
        setProjects(projectsList.reverse());
        setTotalPages(Math.ceil(projectsList.length / projectsPerPage));
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const currentProjects = projects.slice((currentPage - 1) * projectsPerPage, currentPage * projectsPerPage);

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
      <SidebarNav 
        visible={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
      />

      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
        <TopBar onSidebarToggle={() => setSidebarOpen(true)} />

        <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
          <div className="max-w-7xl mx-auto">
            
            <div className="flex flex-col items-center text-center mb-12">
              <PremiumHeader 
                title="Programs" 
                lastWordAccent="Director" 
                subtitle="High-level oversight of global project portfolio and agency performance." 
              />
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setIsProjectModalOpen(true)}
                className="mt-8 flex items-center gap-3 bg-gray-900 text-white px-10 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-orange-600 shadow-2xl shadow-gray-200 transition-all active:scale-95"
              >
                <Plus size={16} strokeWidth={3} /> Initiate New Project
              </motion.button>
            </div>

            {/* KPI Cards Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {[
                { title: "Total Projects", val: projectStats.totalProjects, icon: LayoutPanelTop, accent: "orange", desc: "Full agency scope" },
                { title: "Overdue", val: projectStats.overdue, icon: Clock, accent: "red", desc: "Needs urgent attention" },
                { title: "Completed", val: projectStats.Completed, icon: CheckCircle, accent: "emerald", desc: "Successfully delivered" },
                { title: "On Hold", val: projectStats["On Hold"], icon: PauseCircle, accent: "yellow", desc: "Pending decisions" }
              ].map((stat, i) => (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  key={i}
                >
                  <PremiumStatCard 
                    title={stat.title} 
                    value={stat.val} 
                    icon={<stat.icon className={`text-${stat.accent}-600`} />} 
                    iconBg={`bg-${stat.accent}-50`}
                    description={stat.desc} 
                  />
                </motion.div>
              ))}
            </div>

            {/* Project Calendar */}
            <div className="mb-12">
              <PremiumCard title="Project & Task Milestones" icon={<Calendar size={18} />}>
                <div className="pt-2">
                  <DirectorProjectCalendar />
                </div>
              </PremiumCard>
            </div>

            {/* Analytics Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
              <PremiumCard title="Portfolio Status Distribution" icon={<BarChart2 size={18} />}>
                <div className="py-6">
                  <ReactECharts option={{
                    xAxis: { type: "value", axisLine: { show: false }, axisTick: { show: false }, splitLine: { lineStyle: { type: 'dashed' } } },
                    yAxis: { type: "category", data: ["Completed", "In Progress", "Soon", "Planning", "Overdue", "Hold"], axisLine: { show: false }, axisTick: { show: false }, tick: { fontWeight: 'black', fontSize: 10 } },
                    series: [{
                      type: "bar",
                      data: [projectStats.Completed || 0, projectStats["In Progress"] || 0, projectStats.startingSoon || 0, projectStats.Planning || 0, projectStats.overdue || 0, projectStats["On Hold"] || 0],
                      itemStyle: { 
                        borderRadius: [0, 8, 8, 0],
                        color: (p) => ["#10b981", "#3b82f6", "#fbbf24", "#f97316", "#ef4444", "#facc15"][p.dataIndex] 
                      },
                      barWidth: '40%'
                    }],
                    grid: { left: '15%', right: '5%', bottom: '10%', top: '5%' },
                    tooltip: { trigger: "axis", axisPointer: { type: 'shadow' } },
                  }} style={{ height: "300px" }} />
                </div>
              </PremiumCard>

              <PremiumCard title="Donor Contributions Trends" icon={<PieChartIcon size={18} />}>
                <div className="py-6">
                  <ReactECharts option={{
                    tooltip: { trigger: "item" },
                    legend: { bottom: 0, left: 'center', icon: 'circle', textStyle: { fontSize: 10, fontWeight: 'bold' } },
                    series: [{
                      type: "pie", radius: ["45%", "70%"],
                      avoidLabelOverlap: false,
                      itemStyle: { borderRadius: 12, borderColor: '#fff', borderWidth: 4 },
                      label: { show: false },
                      data: [
                        { value: donorStats.totalDonationsThisYear || 0, name: "Current Fiscal Year", itemStyle: { color: "#f97316" } },
                        { value: (donorStats.totalDonationsAllTime || 0) - (donorStats.totalDonationsThisYear || 0), name: "Legacy Projects", itemStyle: { color: "#e2e8f0" } }
                      ],
                    }]
                  }} style={{ height: "300px" }} />
                </div>
              </PremiumCard>
            </div>

            {/* Portfolio Table */}
            <div className="mb-12">
              <PremiumCard title="High-Level Portfolio Overview" icon={<TrendingUp size={18} />}>
                <div className="overflow-x-auto px-4 mt-6">
                  <table className="w-full text-left">
                    <thead className="text-gray-400 text-[10px] uppercase font-black tracking-widest border-b border-gray-50">
                      <tr>
                        <th className="px-6 py-5">Strategic Project</th>
                        <th className="px-6 py-5">Status</th>
                        <th className="px-6 py-5">Execution Progress</th>
                        <th className="px-6 py-5">Duration</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 text-sm">
                      {currentProjects.length > 0 ? currentProjects.map((project, i) => (
                        <tr key={project._id || i} className="group hover:bg-gray-50/50 transition-colors">
                          <td className="px-6 py-6 font-bold text-gray-800">{project.projectTitle}</td>
                          <td className="px-6 py-6"><StatusBadge status={project.status} /></td>
                          <td className="px-6 py-6 min-w-[200px]"><ProgressBar progress={project.overallProgress} /></td>
                          <td className="px-6 py-6 text-gray-500 font-bold tabular-nums italic text-xs">{project.duration} days remaining</td>
                        </tr>
                      )) : (
                        <tr><td colSpan="4" className="p-16 text-center text-gray-400 font-bold uppercase text-[10px] tracking-widest">No strategic data available in current view</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </PremiumCard>
            </div>

            {/* Stats Summary Area */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 pb-12">
              {[
                { title: "Active Portfolio", v1: "Current", n1: projectStats.totalProjects, v2: "Planning", n2: projectStats.Planning, icon: Activity, accent: "orange" },
                { title: "Procurement Requests", v1: "Pending", n1: operationalStats.requisitions.pending, v2: "Urgent", n2: operationalStats.requisitions.urgent, icon: FileText, accent: "amber" },
                { title: "Staff Leave Status", v1: "Awaiting", n1: leaveStats.pendingRequests, v2: "Approved", n2: leaveStats.approvedRequests, icon: Calendar, accent: "blue" },
                { title: "Global Compliance", v1: "Open Cases", n1: disciplinaryStats.openCases, v2: "Res. Rate", n2: `${disciplinaryStats.resolutionRatePercent}%`, icon: AlertCircle, accent: "red" }
              ].map((group, i) => (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 + i * 0.1 }}
                  key={i}
                  className={`bg-${group.accent}-50/50 border border-${group.accent}-100 rounded-[2rem] p-8 hover:shadow-lg transition-all duration-500`}
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`size-10 rounded-2xl bg-${group.accent}-100/50 flex items-center justify-center text-${group.accent}-600`}>
                      <group.icon size={20} />
                    </div>
                    <h3 className={`font-black text-[10px] uppercase tracking-[0.15em] text-${group.accent}-950/70`}>{group.title}</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center bg-white/50 p-3 rounded-xl">
                      <span className="text-[10px] font-black uppercase text-gray-400 tracking-wider">{group.v1}</span>
                      <span className={`text-sm font-black text-${group.accent}-900`}>{group.n1}</span>
                    </div>
                    <div className="flex justify-between items-center bg-white/50 p-3 rounded-xl">
                      <span className="text-[10px] font-black uppercase text-gray-400 tracking-wider">{group.v2}</span>
                      <span className={`text-sm font-black text-${group.accent}-900`}>{group.n2}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </main>

        <ProjectFormModal isOpen={isProjectModalOpen} onClose={() => setIsProjectModalOpen(false)} onSuccess={() => window.location.reload()} />
      </div>
    </div>
  );
}
